import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trainingApi, type TrainingProgress } from '@/lib/apiClient';
import { mergeProgressRow } from './lib/progress';

/**
 * The tick and personal note on one topic (Phase 3).
 *
 * ⚠️ VISIBILITY IS STATED, NOT IMPLIED. The product owner's call (2026-08-02) was "both,
 * with the visibility made explicit": people track their own progress AND a
 * manager can see it. Recording who has read what is reasonable; recording it
 * while letting the reader assume it is private is not. So the row says so, at
 * the moment someone ticks the box, rather than in a policy nobody reads.
 *
 * ⚠️ A TICK IS STAMPED WITH THE GUIDE REVISION. When the guide moves on, an
 * older tick is shown as "against rev X" rather than silently counting as
 * having read the current text — the forecast instruction was wrong until it
 * was corrected, and anyone who had ticked it would otherwise read as trained
 * on the corrected version. Nothing is ever un-ticked automatically: a typo fix
 * must not wipe the company's training record.
 *
 * ⚠️ `reference` DROPS THE TICK AND KEEPS THE NOTE. Appendix cards are reference,
 * not curriculum — nothing walks a reader through them and they are not in the
 * completion target (see target.ts). Rendering a checkbox there would offer a
 * tick that moves no counter, which is the complaint #283 was raised about.
 * Notes stay, because looking a screen up and writing down what you found is
 * exactly what a reference is for.
 */
export default function TopicProgress({
  topicId, revision, progress, reference = false,
}: {
  topicId: string;
  revision: string;
  progress: TrainingProgress | undefined;
  /** Reference material: show the note control but no completion tick. */
  reference?: boolean;
}) {
  const qc = useQueryClient();
  const done = progress?.completedAt != null;
  const stale = done && progress?.guideRevision != null && progress.guideRevision !== revision;

  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(progress?.note ?? '');
  // Keep the box in step when the server answer arrives or another tab writes.
  useEffect(() => { setNote(progress?.note ?? ''); }, [progress?.note]);

  const save = useMutation({
    // Same scope as guided mode's tick: these write the same rows, so they
    // serialize with each other rather than racing.
    scope: { id: 'training-progress' },
    mutationFn: (dto: { completed?: boolean; note?: string | null }) =>
      trainingApi.setProgress(topicId, { ...dto, guideRevision: revision }),
    // Only this topic's row is taken from the response — see mergeProgressRow.
    onSuccess: (rows) =>
      qc.setQueryData<TrainingProgress[]>(['training-progress'], (prev) =>
        mergeProgressRow(prev, rows, topicId)),
  });

  return (
    <div className="tp-row">
      {!reference && (
        <label className="tp-tick">
          <input
            type="checkbox"
            checked={done}
            disabled={save.isPending}
            onChange={(e) => save.mutate({ completed: e.target.checked })}
          />
          <span>{done ? 'Done' : 'Mark as done'}</span>
        </label>
      )}

      {!reference && stale && (
        <span
          className="tp-stale"
          title={`You ticked this against revision ${progress!.guideRevision}. The guide is now revision ${revision}, so this topic may have changed since.`}
        >
          against rev {progress!.guideRevision}
        </span>
      )}

      <button type="button" className="tp-note-btn" onClick={() => setNoteOpen((v) => !v)}>
        {progress?.note ? 'Edit note' : 'Add note'}
      </button>

      {noteOpen && (
        <div className="tp-note">
          <textarea
            value={note}
            maxLength={2000}
            rows={3}
            placeholder="Your own note on this topic — anything worth remembering next time."
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="tp-note-actions">
            <span className="tp-vis">Your manager can see your notes and progress.</span>
            <button type="button" className="tp-btn" onClick={() => setNoteOpen(false)} disabled={save.isPending}>Cancel</button>
            <button
              type="button"
              className="tp-btn primary"
              disabled={save.isPending}
              onClick={() => save.mutate({ note }, { onSuccess: () => setNoteOpen(false) })}
            >
              {save.isPending ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      )}

      {!noteOpen && progress?.note && (
        <p className="tp-note-read">{progress.note}</p>
      )}
    </div>
  );
}
